import { StatusDropdown } from "components";
import UserWithAvatar from "components/Atoms/UserWithAvatar";
import NotValidatedRow from "components/DetailPage/NotValidatedRow";
import { useFreefieldState } from "components/Freefields/hooks";
import { GoogleMapLocation } from "components/GoogleMap/GoogleMapLocation";
import { ViewOnMap } from "components/GoogleMap/ViewOnMap";
import { SidePanelDetailSectionTemplate } from "components/MainSectionTemplate/SidePanelSection/SidePanelDetailSectionTemplate";
import { RenderEditOptions } from "components/MainSectionTemplate/types";
import { ShowDates } from "components/showdatepicker/models";
import { ShowDateAndTimePicker } from "components/showdatepicker/ShowDateAndTimePicker";
import SmallDetails from "components/SmallDetails/SmallDetails";
import Status from "components/Status/Status";
import TagList from "components/Tag/TagList";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import { ExclusivityConflictsForShowSidePanel } from "exclusivityclause/ExclusivityConflictsForShowSidePanel";
import { ExclusivityCheckFields } from "exclusivityclause/utils";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { ShowDateTimeShowInfo } from "show/components/ShowDateTime";
import { mapShowInfoToUpdateShowInternalInstruction, mapShowToShowInfo, ShowInfo } from "show/edit/utils";
import { formatCurrency } from "utilities";
import { BookkeepingSidePanel } from "../Bookkeeping/BookkeepingSidePanel";
import { ArtistSidePanel, PromoterSidePanel } from "../Company/CompanySidePanel";
import { SidePanelContactList } from "../contact/SidePanelContactList";
import { ProductionSidePanel } from "../Production/ProductionSidePanel";
import { sidePanelFreeFieldSections } from "../SidePanelFreeFieldSection";
import { VenueSidePanel } from "../Venue/VenueSidePanel";
import { invalidateShowsCache } from "utilities/api/showQuery";
import { useQueryClient } from "@tanstack/react-query";

interface ShowSidePanelDetailsProps {
  data: server.dto.ShowSidePanelData

  canEdit: boolean;
}

const getUpdateShowInternalInstruction = (showInfo: ShowInfo, freeFields: server.dto.FreeFieldValue[], show: server.dto.Show): server.dto.UpdateShowInternalEnvironmentInstruction => ({
  showId: show.showId,
  ...mapShowInfoToUpdateShowInternalInstruction(showInfo, freeFields, show.production),
})

export const ShowSidePanelDetails = (props: ShowSidePanelDetailsProps) => {
  const products = props.data.products;
  const show = props.data.show;
  const exclusivityClause = props.data.exclusivityClause;

  const isInternalUser = site.currentEnvironment.type === "internal";

  const costsExTax = products.subtotal.costs?.amount;
  const salesExTax = products.subtotal.sales?.amount;

  const hasProducts = products.collection.length > 0;

  const [showInfo, setShowInfo] = useState<ShowInfo>(mapShowToShowInfo(show));
  const freefieldState = useFreefieldState(show.freeFields, props.data.freeFieldCategories, props.data.freeFieldDefinitionPermissions);

  const exclusivityCheckFields: ExclusivityCheckFields = {
    artistId: showInfo.artist?.artistId,
    excludeShowId: show.showId,
    exclusivityClause,
    showStartDisplay: showInfo.showDates.startDisplayDate,
    showEndDisplay: showInfo.showDates.endDisplayDate,
    // TODO: These two should come from the show info
    venueLocation: show.venueGeoLocation,
    venueId: show.venue?.companyId,
    appointmentPhase: showInfo.status.appointmentPhase,
  }

  useEffect(() => {
    setShowInfo(mapShowToShowInfo(show));
  }, [props.data.show]);

  const queryClient = useQueryClient();

  const onValueChanged = (key: string, value: any) => {
    if (key === "eventName") {
      setShowInfo({ ...showInfo, eventName: value });
    } else if (key === "status") {
      setShowInfo({ ...showInfo, status: value });
    } else if (key === "dateAndTime") {
      setShowInfo({ ...showInfo, showDates: value });
    }
  }

  const onSuccessfulUpdate = async () => {
    invalidateShowsCache(queryClient);
  }

  const canInPageEdit = props.canEdit && show.permissions.canModify && isInternalUser;
  const canApprove = show.permissions.canApprove && isInternalUser;

  const approvelCardSubLabel = show.validationStatus === "notValidatedInputByExternal"
    ? `${RESX.Planning.InsertedBy} ${show.createdUser.fullName} ${moment.utc(show.createdUtc).fromNow()}`
    : `${RESX.Planning.InputFromWebform}`;

  return <SidePanelDetailSectionTemplate
    isInPageEditable={canInPageEdit}
    onValueChanged={onValueChanged}
    onSuccessfulUpdate={onSuccessfulUpdate}
    fields={[{
      key: "isValidated",
      label: "",
      hideLabel: true,
      hidden: show.isValidated,
      render: () => <NotValidatedRow
        canApprove={canApprove}
        sublabel={approvelCardSubLabel}
        approveEndpoint={() => window.CoreApi.Show.Approve({
          showId: show.showId,
        })}
      />
    }, {
      key: "artist",
      label: site.currentEnvironment.functionGroups.artist.singular,
      hidden: !site.currentEnvironment.functionGroups.artist.enabled,
      isEmpty: !show.artist,
      render: () => <ArtistSidePanel
        companyId={show.artist.companyId}
        trigger={<a>{show.artist.name}</a>}
        fallbackIfNoPermission={<>{show.artist.name}</>}
      />
    }, {
      key: "attendees",
      label: RESX.Planning.Attendees,
      isEmpty: show.attendees.length === 0,
      render: () => <SidePanelContactList contacts={show.attendees} />
    }, {
      key: "dateAndTime",
      label: RESX.DateTime.lblDate,
      render: () => <ShowDateTimeShowInfo showDates={showInfo.showDates} />,
      inPageEdit: {
        render: ({ value, onChange }: RenderEditOptions<ShowDates>) => <div style={{ paddingLeft: 0 }} className="main-section-template-fields-padding">
          <ShowDateAndTimePicker
            timePickerPlacehoder={RESX.Show.TimeOfPerformance}
            showDates={value}
            onChange={(showDates) => { onChange(showDates); }}
          />
        </div>,
        fullWidth: true,
        type: "confirmOrCancelButton",
        initialValue: showInfo.showDates,
        submitChange: (showDates: ShowDates) => window.CoreApi.Show.UpdateForInternalEnvironment(
          getUpdateShowInternalInstruction({ ...showInfo, showDates }, freefieldState.editableFreefieldValues, show),
        )
      }
    }, {
      key: "status",
      label: RESX.Status.resxStatus,
      render: () => <Status status={showInfo.status} displayPhaseIcon />,
      inPageEdit: {
        render: ({ value, onChange, cancel }: RenderEditOptions<server.dto.StatusSmall>) => <StatusDropdown
          mainArea="show"
          options={undefined}
          value={value}
          onChange={(value) => onChange(value, true)}
          onEsc={() => cancel()}
          config={{
            focusOnMount: true,
            openMenuOnFocus: true,
          }}
        />,
        fullWidth: true,
        type: "custom",
        initialValue: showInfo.status,
        submitChange: (status: server.dto.StatusSmall) => window.CoreApi.Show.UpdateForInternalEnvironment(
          getUpdateShowInternalInstruction({ ...showInfo, status }, freefieldState.editableFreefieldValues, show)
        )
      }
    }, {
      key: "exclusivity",
      label: RESX.Exclusivity.resxExclusivity,
      hidden: !isInternalUser,
      render: () => <ExclusivityConflictsForShowSidePanel
        show={show}
        exclusivityCheckFields={exclusivityCheckFields}
      />
    }, {
      key: "eventName",
      label: RESX.Show.NameOfEvent,
      isEmpty: !showInfo.eventName,
      render: () => <>{showInfo.eventName}</>,
      inPageEdit: {
        render: ({ value, onChange, submit, cancel }) => <OptionalTextInput
          focusOnMount
          maxLength={serverReference.constants.show.maxEventNameLength}
          value={value}
          onChange={(title) => { onChange(title); }}
          onEnter={() => submit()}
          onEsc={() => cancel()}
        />,
        fullWidth: true,
        type: "confirmOrCancelButton",
        initialValue: showInfo.eventName ?? "",
        submitChange: (eventName: string) => window.CoreApi.Show.UpdateForInternalEnvironment(
          getUpdateShowInternalInstruction({ ...showInfo, eventName }, freefieldState.editableFreefieldValues, show),
        )
      }
    }, {
      key: "venue",
      label: site.currentEnvironment.functionGroups.venue.singular,
      isEmpty: !show.venue,
      hidden: !site.currentEnvironment.functionGroups.venue.enabled,
      render: () => <VenueSidePanel
        venueId={show.venue.companyId}
        trigger={<>
          <a>{show.venue.name}</a>
          <div className="text-sublabel">{show.venue.cityCountry}</div>
        </>}
        fallbackIfNoPermission={<>
          <div>{show.venue.name}</div>
          <div className="text-sublabel">{show.venue.cityCountry}</div>
        </>}
      />
    }, {
      key: "promoter",
      label: site.currentEnvironment.functionGroups.promoter.singular,
      isEmpty: !show.promoter,
      hidden: !site.currentEnvironment.functionGroups.promoter.enabled,
      render: () => <PromoterSidePanel
        companyId={show.promoter.companyId}
        trigger={<a>{show.promoter.name}</a>}
        fallbackIfNoPermission={<>{show.promoter.name}</>}
      />
    }, {
      key: "production",
      label: site.currentEnvironment.production.singular,
      isEmpty: !show.production,
      hidden: !site.currentEnvironment.production.enabled,
      render: () => <ProductionSidePanel
        productionId={show.production.productionId}
        trigger={<a>{show.production.name}</a>}
        fallbackIfNoPermission={<span>{show.production.name}</span>}
      />
    }, {
      key: "owner",
      label: RESX.GeneralLabels.Owner,
      render: () => <UserWithAvatar user={show.owner} avatarSize="avatar-30" />
    }, {
      key: "bookkeeping",
      label: RESX.Bookkeeping.resxBookkeeping,
      isEmpty: !show.bookkeeping,
      hidden: !isInternalUser,
      render: () => <BookkeepingSidePanel
        bookkeepingId={show.bookkeeping.bookkeepingId}
        trigger={<>
          <a>{show.bookkeeping.name}</a>
          <div className="text-sublabel">{show.bookkeeping.code}</div>
        </>}
        fallbackIfNoPermission={<>
          <div>{show.bookkeeping.name}</div>
          <div className="text-sublabel">{show.bookkeeping.code}</div>
        </>}
      />
    }, {
      key: "costCenter",
      label: RESX.CostCenter.resxCostCenter,
      isEmpty: !show.costCenter,
      hidden: !isInternalUser || !site.currentEnvironment.hasCostCenters,
      render: () => <SmallDetails
        label={show.costCenter.title}
        subLabel={show.costCenter.internalId}
      />
    }, {
      key: "sales",
      label: RESX.Financial.Sales,
      isEmpty: !hasProducts,
      hidden: !site.permissions.show.canReadSales || !site.currentEnvironment.show.displaySales,
      render: () => <span>{formatCurrency(salesExTax, show.currency, { hideCents: true })}</span>
    }, {
      key: "costs",
      label: RESX.Financial.Costs,
      isEmpty: !hasProducts,
      hidden: !site.permissions.show.canReadCosts || !site.currentEnvironment.show.displayCosts,
      render: () => <span>{formatCurrency(costsExTax, show.currency, { hideCents: true })}</span>
    }, {
      key: "projectCode",
      label: RESX.Show.ProjectCode,
      isEmpty: !show.projectCode,
      hidden: !isInternalUser || site.currentEnvironment.projectCode === "hidden",
      render: () => <span>{show.projectCode}</span>
    }, {
      key: "publication",
      label: RESX.SelectionManager.Publication,
      render: () => <span>{show.publication.text}</span>
    }, {
      key: "tags",
      label: RESX.Tag.resxTag,
      isEmpty: show.tags.length === 0,
      render: () => <TagList tags={show.tags} length="short" />
    },
    ...sidePanelFreeFieldSections(props.data.freeFieldCategories, show.freeFields)]}
    additionalContent={<>
      {show.venueGeoLocation && <>
        <ViewOnMap height={400}>
          <GoogleMapLocation pin={{
            type: "show",
            phase: "unknown"
          }} location={show.venueGeoLocation} />
        </ViewOnMap>
      </>}
    </>}
  />
}
